/**
 * Every document the Action Plan can open, by id, and which form a client needs (owner, 3 Oct 2026).
 *
 * A journey step names document ids; this file says what each one is called on the chip, whether it
 * applies to this client, and builds it. One place, so the step chips, the Quick Actions and the tests
 * agree — and so a chip is never a label with nothing behind it again.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { AnswerSheet } from './answerSheet.ts';
import { openAnswerSheet } from './answerSheet.ts';
import {
    buildAssessmentApplication, buildGrannyFlatReport, buildVillageReport,
    buildMoveInChecklist, buildVillageContractChecklist, buildMovingChecklist,
} from './otherDocuments.ts';
import { type Who, num, isCouple, personOf, ownsHome, likelyCareEntrant } from './profileFacts.ts';
import { openDownsizerForm } from './officialForms.ts';

export type MeansForm = 'SA485' | 'SA457' | 'SA456' | 'none';

export const MYGOV_URL = 'https://my.gov.au';

/**
 * WHICH MEANS FORM (Services Australia's own "When to use this form" rules):
 *   residential care — receives a means-tested payment and owns a home → SA485; receives one and
 *   does not own a home → none (assessed automatically); does not receive one → SA457.
 *   Support at Home — receives a means-tested payment → none; does not → SA456.
 * `receivesPension` is the tool's estimate of entitlement, which is why the chooser always offers
 * the other form too.
 */
export function meansFormFor(ff: DraftFactFind, setting: 'residential' | 'home', receivesPension: boolean): MeansForm {
    if (setting === 'home') return receivesPension ? 'none' : 'SA456';
    if (!receivesPension) return 'SA457';
    return ownsHome(ff) ? 'SA485' : 'none';
}

export interface SavedPlanLike {
    type?: string;
    createdAt?: string;
    inputs?: Record<string, unknown>;
}

/** The latest saved plan of a type — the documents quote the plan the client chose, not a default. */
function latestPlan(saved: SavedPlanLike[] | undefined, type: string): SavedPlanLike | null {
    const rows = (saved || []).filter((s) => s?.type === type);
    if (!rows.length) return null;
    return rows.slice().sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || ''))).pop() || null;
}

export function downsizerContributionFromPlan(saved: SavedPlanLike[] | undefined, who: Who): number {
    const details = (latestPlan(saved, 'downsizer')?.inputs?.details || {}) as Record<string, unknown>;
    return num(who === 'partner' ? details.partnerDownsizerContribution : details.downsizerContribution);
}

export function villageEntryFromPlan(saved: SavedPlanLike[] | undefined): number {
    const vi = (latestPlan(saved, 'retirement_village')?.inputs?.villageInputs || {}) as Record<string, unknown>;
    return num(vi.entryFee);
}

export type DocId =
    | 'assessment_application' | 'assessment_application_home' | 'means_assessment' | 'means_assessment_home' | 'move_in_checklist'
    | 'village_contract_checklist' | 'village_report' | 'moving_checklist_village'
    | 'downsizer_contribution' | 'moving_checklist_sale'
    | 'family_guide' | 'solicitor_brief' | 'granny_flat_report'
    | 'pension_claim' | 'report_change';

export interface DocContext {
    factFind: DraftFactFind;
    savedStrategies?: SavedPlanLike[];
    receivesPension: boolean;
    /** Today's Age Pension a year, as the Dashboard works it out: what a plan's pension is compared with. */
    currentPension?: number;
}

/** What a chip does: open a sheet, open the means chooser, or go to a page in the app. */
export type DocAction =
    | { kind: 'sheet'; build: () => AnswerSheet; fileStem: string }
    | { kind: 'means'; setting: 'residential' | 'home' }
    /** The official form itself, filled in from the profile (owner, 3 Oct 2026). */
    | { kind: 'official'; open: () => Promise<void> }
    /** Claiming the Age Pension: the chooser (online, or the SA002 and SA369 pre-filled). */
    | { kind: 'pension_claim' }
    /** Somewhere outside the app, such as myGov. */
    | { kind: 'link'; url: string }
    | { kind: 'page'; page: string };

export interface DocChip {
    id: DocId;
    label: string;
    action: DocAction;
}

/** The chip for a document id, for this client — or null when it does not apply to them. */
export function docChip(id: DocId, ctx: DocContext): DocChip | null {
    const ff = ctx.factFind;
    const now = () => new Date().toISOString();
    const first = personOf(ff, 'client').first || 'Client';
    switch (id) {
        case 'assessment_application':
        case 'assessment_application_home':
            return { id, label: 'Assessment application details', action: { kind: 'sheet', build: () => buildAssessmentApplication(ff, { who: likelyCareEntrant(ff), generatedAt: now(), forHomeCare: id === 'assessment_application_home' }), fileStem: `AgedCareAssessment_${first}` } };
        case 'means_assessment': {
            const form = meansFormFor(ff, 'residential', ctx.receivesPension);
            return { id, label: form === 'none' ? 'Which form do I need?' : `${form} pre-filled`, action: { kind: 'means', setting: 'residential' } };
        }
        case 'means_assessment_home': {
            const form = meansFormFor(ff, 'home', ctx.receivesPension);
            return { id, label: form === 'none' ? 'Which form do I need?' : `${form} pre-filled`, action: { kind: 'means', setting: 'home' } };
        }
        case 'move_in_checklist':
            return { id, label: 'Move-in checklist', action: { kind: 'sheet', build: () => buildMoveInChecklist(ff, { generatedAt: now() }), fileStem: `MoveInChecklist_${first}` } };
        case 'village_contract_checklist':
            return { id, label: 'Contract questions', action: { kind: 'sheet', build: () => buildVillageContractChecklist(ff, { generatedAt: now() }), fileStem: `VillageContractQuestions_${first}` } };
        case 'village_report':
            return { id, label: 'What to tell Services Australia', action: { kind: 'sheet', build: () => buildVillageReport(ff, { generatedAt: now(), entryContribution: villageEntryFromPlan(ctx.savedStrategies) }), fileStem: `VillageMove_ServicesAustralia_${first}` } };
        case 'moving_checklist_village':
            return { id, label: 'Moving checklist', action: { kind: 'sheet', build: () => buildMovingChecklist(ff, { generatedAt: now(), selling: num(ff?.home?.value) > 0 }), fileStem: `MovingChecklist_${first}` } };
        case 'moving_checklist_sale':
            return { id, label: 'Selling and moving checklist', action: { kind: 'sheet', build: () => buildMovingChecklist(ff, { generatedAt: now(), selling: true }), fileStem: `MovingChecklist_${first}` } };
        case 'downsizer_contribution':
            // Only where the plan the client saved actually tops up super: the form is meaningless otherwise.
            if (downsizerContributionFromPlan(ctx.savedStrategies, 'client') <= 0) return null;
            return { id, label: 'Downsizer contribution form (NAT 75073)', action: { kind: 'official', open: () => openDownsizerForm(ff, 'client', downsizerContributionFromPlan(ctx.savedStrategies, 'client')) } };
        case 'family_guide':
            return { id, label: 'Family discussion guide', action: { kind: 'page', page: 'GrannyFlatPage' } };
        case 'solicitor_brief':
            return { id, label: 'Solicitor brief', action: { kind: 'page', page: 'GrannyFlatPage' } };
        case 'pension_claim':
            return { id, label: 'Claim the Age Pension (SA002 and SA369 pre-filled)', action: { kind: 'pension_claim' } };
        case 'report_change':
            return { id, label: 'Report it in myGov', action: { kind: 'link', url: MYGOV_URL } };
        case 'granny_flat_report':
            return { id, label: 'What to tell Services Australia', action: { kind: 'sheet', build: () => buildGrannyFlatReport(ff, { generatedAt: now() }), fileStem: `GrannyFlat_ServicesAustralia_${first}` } };
        default:
            return null;
    }
}

export function openDocChipSheet(chip: DocChip): void {
    if (chip.action.kind === 'sheet') openAnswerSheet(chip.action.build(), chip.action.fileStem);
}

/** The partner's own downsizer form, where the plan makes a contribution for them too. */
export function partnerDownsizerChip(ctx: DocContext): DocChip | null {
    if (!isCouple(ctx.factFind) || downsizerContributionFromPlan(ctx.savedStrategies, 'partner') <= 0) return null;
    const name = personOf(ctx.factFind, 'partner').first || 'Partner';
    return { id: 'downsizer_contribution', label: `NAT 75073 for ${name}`, action: { kind: 'official', open: () => openDownsizerForm(ctx.factFind, 'partner', downsizerContributionFromPlan(ctx.savedStrategies, 'partner')) } };
}
